import type {PropertyDefinition, PropertyValuesRecord, Repository} from '@github-ui/custom-properties-types'
import {human} from '@github-ui/formatters'
import {ListItem, ListView} from '@github-ui/list-view'
import {type Icon, PencilIcon, RepoIcon, RepoLockedIcon} from '@primer/octicons-react'
import {ActionList, Box, Button, IconButton, Pagination, Text} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {EditRepoPropertiesDialog} from '../components/EditRepoPropertiesDialog'

export function ReposPropertiesList({
  repositoryCount,
  repos,
  pageCount,
  definitions,
  page,
  query,
  onPageChange,
  onReposSelection,
  onEditSuccess,
}: {
  repositoryCount: number
  repos: Repository[]
  pageCount: number
  definitions: PropertyDefinition[]
  page: number
  query: string
  onPageChange: (page: number) => void
  onReposSelection: () => void
  onEditSuccess: () => void
}) {
  const [editingRepos, setEditingRepos] = useState<Repository[]>([])
  const [selectedRepoIds, setSelectedRepoIds] = useState<Set<number>>(new Set())
  const clickedEditButtonRef = useRef<null | HTMLButtonElement>(null)
  const editableProperties = useMemo(() => definitions.map(d => d.propertyName), [definitions])

  const dismissDialog = () => setEditingRepos([])

  useEffect(() => {
    setSelectedRepoIds(new Set())
  }, [page, query])

  const onRepoSelectToggle = useCallback(
    (repo: Repository) => {
      const update = new Set(selectedRepoIds)
      if (selectedRepoIds.has(repo.id)) {
        update.delete(repo.id)
        setSelectedRepoIds(update)
      } else {
        update.add(repo.id)
        setSelectedRepoIds(update)
      }
    },
    [selectedRepoIds],
  )

  const selectRepos = useCallback(
    (repositorySelections: Repository[]) => {
      onReposSelection()
      setEditingRepos(repositorySelections)
    },
    [onReposSelection, setEditingRepos],
  )

  const selectedRepos = useMemo(() => repos.filter(repo => selectedRepoIds.has(repo.id)), [repos, selectedRepoIds])
  const hasSelectedRepos = selectedRepos.length > 0

  const metadata = (
    <ListView.Metadata
      onToggleSelectAll={isSelectAllChecked => {
        return isSelectAllChecked
          ? setSelectedRepoIds(new Set(repos.map(repo => repo.id)))
          : setSelectedRepoIds(new Set())
      }}
      title={<Text sx={{fontWeight: 'bold'}}>{reposCountLabel(repositoryCount)}</Text>}
      actionsLabel="Actions"
      actions={[
        {
          key: 'edit',
          render(isOverflowMenu) {
            if (!hasSelectedRepos) {
              return <></>
            }

            return isOverflowMenu ? (
              <ActionList.Item onClick={() => selectRepos(selectedRepos)}>
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit properties
              </ActionList.Item>
            ) : (
              <Button leadingVisual={PencilIcon} onClick={() => selectRepos(selectedRepos)}>
                Edit properties
              </Button>
            )
          },
        },
      ]}
    />
  )

  return (
    <>
      <Box sx={{border: '1px solid', borderColor: 'border.muted', borderRadius: 2}} data-testid="repos-properties-list">
        <ListView
          isSelectable
          variant="compact"
          metadata={metadata}
          title="Repositories"
          titleHeaderTag="h2"
          singularUnits="repository"
          pluralUnits="repositories"
          totalCount={repos.length}
        >
          {repos.map(repo => (
            <ReposPropertiesListItem
              key={repo.id}
              {...repo}
              selected={selectedRepoIds.has(repo.id)}
              onEditButtonClick={el => {
                selectRepos([repo])
                clickedEditButtonRef.current = el
              }}
              onSelect={() => onRepoSelectToggle(repo)}
            />
          ))}
        </ListView>
        {!!editingRepos.length && (
          <EditRepoPropertiesDialog
            definitions={definitions}
            editableProperties={editableProperties}
            onDismiss={() => {
              clickedEditButtonRef.current?.focus()
              dismissDialog()
            }}
            onSuccess={() => {
              onEditSuccess()
              dismissDialog()
            }}
            editingRepos={editingRepos}
          />
        )}
      </Box>
      <Pagination
        pageCount={pageCount}
        currentPage={page}
        onPageChange={(e, newPage) => {
          // Avoid adding hash parameters
          e.preventDefault()

          window.scrollTo({top: 0, behavior: 'smooth'})
          onPageChange(newPage)
        }}
      />
    </>
  )
}

function ReposPropertiesListItem({
  name,
  description,
  properties = {},
  selected,
  visibility,
  onEditButtonClick,
  onSelect,
}: {
  name: string
  description: string | null
  properties?: PropertyValuesRecord
  visibility: Repository['visibility']
  onEditButtonClick: (el: HTMLButtonElement) => void
  selected: boolean
  onSelect: () => void
}) {
  const propertiesLength = Object.keys(properties).length

  return (
    <ListItem
      title={<ListItem.Title value={name} />}
      onSelect={onSelect}
      isSelected={selected}
      metadataContainerSx={{pr: 0}}
      metadata={
        <>
          <ListItem.Metadata alignment="right" sx={{pl: 2, pr: 0}}>
            <Text>
              {propertiesLength
                ? `${propertiesLength} ${propertiesLength === 1 ? 'property' : 'properties'}`
                : 'No properties'}
            </Text>
          </ListItem.Metadata>
          <ListItem.Metadata alignment="right" variant="primary" sx={{p: 0}}>
            <IconButton
              icon={PencilIcon}
              variant="invisible"
              aria-label={`Edit ${name}'s properties`}
              onClick={e => onEditButtonClick(e.currentTarget)}
            />
          </ListItem.Metadata>
        </>
      }
    >
      <ListItem.LeadingContent>
        <ListItem.LeadingVisual color="fg.muted" icon={visibilityMap[visibility]} />
      </ListItem.LeadingContent>

      <ListItem.MainContent>
        <ListItem.Description
          sx={{
            maxWidth: '100%',
            display: 'flex',
          }}
        >
          {description && (
            <ListItem.DescriptionItem
              sx={{whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', display: 'block'}}
            >
              {description}
            </ListItem.DescriptionItem>
          )}
        </ListItem.Description>
      </ListItem.MainContent>
    </ListItem>
  )
}

const visibilityMap: Record<Repository['visibility'], Icon> = {
  private: RepoLockedIcon,
  public: RepoIcon,
  internal: RepoIcon,
}

function reposCountLabel(reposCount: number) {
  return `${human(reposCount)} ${reposCount === 1 ? 'repository' : 'repositories'}`
}

try{ ReposPropertiesList.displayName ||= 'ReposPropertiesList' } catch {}
try{ ReposPropertiesListItem.displayName ||= 'ReposPropertiesListItem' } catch {}